import { type ReactElement, useEffect, useRef, useState } from 'react'
import { isDefined, type UniversalSearchScope } from '@terros/common'
import { createSpotlight, Spotlight } from '@mantine/spotlight'
import { getDefaultZIndex, Select, VisuallyHidden } from '@mantine/core'
import { useCmdHotKey, useDebounce, useUniversalSearch, useToastError, type WebSearchScope } from '@hooks'
import { SizePrimitives, useProfile, useTranslation, useUniversalSearchQuery } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TButton } from '../buttons'
import { UniversalSearchContent } from './UniversalSearchContent'
import classes from './spotlight.module.css'

export const UniversalSearchModal = (): ReactElement => {
  const { t } = useTranslation()
  const { isTantalim } = useProfile()
  const [{ scope, query: searchValue = '' }, setUniversalSearch] = useUniversalSearch()
  const [[store, spotlight]] = useState(createSpotlight)
  const opened = Boolean(scope)
  const lastScope = useRef<WebSearchScope>(scope ?? 'all')
  const activeScope = scope ?? lastScope.current
  const destinationsOnly = activeScope === 'destinations'
  const scopeOptions: { value: WebSearchScope; label: string; placeholder: string }[] = [
    { value: 'all', label: t('Everything'), placeholder: t('Search…') },
    { value: 'destinations', label: t('App destinations'), placeholder: t('Search app destinations…') },
    { value: 'Account', label: t('Accounts'), placeholder: t('Search accounts…') },
    { value: 'User', label: t('People'), placeholder: t('Search people…') },
    { value: 'Calendar', label: t('Calendar'), placeholder: t('Search calendar…') },
    { value: 'Team', label: t('Teams'), placeholder: t('Search teams…') },
    ...(isTantalim ? [{ value: 'Company' as const, label: t('Companies'), placeholder: t('Search companies…') }] : []),
  ]
  const query = useDebounce(searchValue, 500)
  const scopes: UniversalSearchScope[] | undefined =
    isDefined(scope) && scope !== 'all' && scope !== 'destinations' ? [scope] : undefined
  const {
    data: results = [],
    isLoading,
    error,
  } = useUniversalSearchQuery({ query, scopes }, opened && !destinationsOnly)
  useToastError(error)

  useEffect(() => {
    if (isDefined(scope)) {
      lastScope.current = scope
      spotlight.open()
    } else spotlight.close()
  }, [opened, scope, spotlight])

  // The URL owns open state. Actions navigate without writing nuqs state: a queued nuqs write replays the
  // pre-navigation pathname and undoes the route change, so only Escape/outside click (opened still true) closes here.
  const close = (): void => setUniversalSearch({ scope: null })
  const open = (): void => setUniversalSearch({ scope: lastScope.current })
  useCmdHotKey('k', () => (opened ? close() : open()))

  const isMac = typeof navigator !== 'undefined' && navigator.userAgent.includes('Mac')
  const shortcutDisplay = isMac ? '⌘K' : 'Ctrl+K'

  return (
    <>
      <TButton
        icon='search'
        miw={{ base: undefined, sm: 300 }}
        variant='secondary'
        rightSection={
          <span style={{ opacity: 0.6, fontSize: '0.75rem', fontWeight: 'normal' }}>{shortcutDisplay}</span>
        }
        onClick={open}
      >
        {t('Search...')}
      </TButton>
      <Spotlight.Root
        store={store}
        shortcut={null}
        query={searchValue}
        onQueryChange={(value) => setUniversalSearch({ scope, query: value })}
        onSpotlightClose={() => {
          if (opened) close()
        }}
        closeOnActionTrigger={false}
        clearQueryOnClose={false}
        size={680}
        radius={20}
        yOffset='12vh'
        overlayProps={{ backgroundOpacity: 0.5, blur: 3 }}
        classNames={classes}
        scrollable
        maxHeight='min(60vh, 520px)'
        title={<VisuallyHidden>{t('Search the app')}</VisuallyHidden>}
        styles={{ header: { padding: 0, minHeight: 0, height: 0 } }}
      >
        <Spotlight.Search
          size='md'
          aria-label={t('Search entities and app destinations')}
          onKeyDown={(event) => {
            if (
              event.nativeEvent.isComposing ||
              event.repeat ||
              event.altKey ||
              event.shiftKey ||
              event.key.toLowerCase() !== 'i' ||
              !(isMac ? event.metaKey : event.ctrlKey)
            )
              return
            const list = document.getElementById(store.getState().listId)
            const action = list
              ?.querySelector('[data-selected]')
              ?.closest('[data-search-result]')
              ?.querySelector<HTMLButtonElement>('[data-impersonate]')
            if (action) {
              event.preventDefault()
              action.click()
            }
          }}
          placeholder={scopeOptions.find((option) => option.value === activeScope)?.placeholder ?? t('Search…')}
          rightSectionWidth={170}
          rightSectionPointerEvents='auto'
          rightSection={
            <Select
              aria-label={t('Search scope')}
              value={activeScope}
              data={scopeOptions.map(({ value, label }) => ({ value, label }))}
              allowDeselect={false}
              variant='unstyled'
              w={158}
              rightSectionWidth={24}
              classNames={{ root: classes.scope, input: classes.scopeInput }}
              comboboxProps={{ zIndex: getDefaultZIndex('max') + 1 }}
              onChange={(value) => {
                const selected = scopeOptions.find((option) => option.value === value)
                if (selected) setUniversalSearch({ scope: selected.value })
              }}
            />
          }
          leftSection={<MaterialSymbolIcon icon='search' size={SizePrimitives['6']} />}
        />
        <UniversalSearchContent
          store={store}
          key={scope}
          scope={activeScope}
          results={!destinationsOnly && query === searchValue ? results : []}
          isLoading={!destinationsOnly && Boolean(searchValue.trim()) && (query !== searchValue || isLoading)}
          error={!destinationsOnly && Boolean(error) && query === searchValue}
          searchValue={searchValue}
          close={close}
        />
      </Spotlight.Root>
    </>
  )
}
